import {
  AllocationPaymentItem,
  AllocationPaymentTargetAccount,
  GetAllocationPaymentDetailsResponse,
  isDefined,
  Keys,
  Loadable,
  LoadableState,
  NbSelectItemOption,
} from '@eih-netbroker/common';
import { useMemo } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { ErrorWidgetContent } from '../../../../shared/components/error-widget-content/ErrorWidgetContent';
import { FeedbackBox } from '../../../../shared/components/feedback-box/FeedbackBox';
import { NbLoader } from '../../../../shared/components/loader/Loader';
import { ModalFooter } from '../../../../shared/components/modal/ModalFooter';
import { NbSelect } from '../../../../shared/components/select/Select';
import { CashActionNoSourceOrTargetAccountErrorFeedback } from '../common/CashActionNoSourceOrTargetAccountErrorFeedback';
import { CashActionParamEditor } from '../common/CashActionParamEditor';
import classes from './CashAllocationParamEditorWrapper.module.scss';

type CashAllocationParamEditorWrapperProps = {
  amount: string;
  allocationDetails: LoadableState<GetAllocationPaymentDetailsResponse>;
  numberInputError?: string;
  selectedSourceAccount?: AllocationPaymentItem;
  selectedTargetAccount?: AllocationPaymentTargetAccount;
  selectedCurrency: string;
  handleCurrencyChange: (currency: string) => void;
  currencyOptions: Array<NbSelectItemOption<string>>;
  sourceAccountOptions?: LoadableState<Array<NbSelectItemOption<AllocationPaymentItem>>>;
  targetAccountOptions?: LoadableState<Array<NbSelectItemOption<AllocationPaymentTargetAccount>>>;
  handleAmountChange: (value: string) => void;
  handleSourceAccountChange?: (value: string | AllocationPaymentItem) => void;
  handleTargetAccountChange: (value: AllocationPaymentTargetAccount | string) => void;
  handleSubmit: () => void;
  refreshAllocationDetails: () => Promise<void>;
};

export const CashAllocationParamEditorWrapper = ({
  amount,
  handleAmountChange,
  handleTargetAccountChange,
  selectedTargetAccount,
  targetAccountOptions,
  selectedCurrency,
  currencyOptions,
  handleCurrencyChange,
  numberInputError,
  handleSourceAccountChange,
  sourceAccountOptions,
  selectedSourceAccount,
  allocationDetails,
  handleSubmit,
  refreshAllocationDetails,
}: CashAllocationParamEditorWrapperProps) => {
  const { translation } = useLocale();

  const noTargetAccountsAvailable = useMemo<boolean>(
    () =>
      isDefined(sourceAccountOptions) &&
      isDefined(selectedSourceAccount) &&
      isDefined(targetAccountOptions) &&
      sourceAccountOptions.isLoaded &&
      targetAccountOptions.isLoaded &&
      targetAccountOptions.length === 0,
    [selectedSourceAccount, sourceAccountOptions, targetAccountOptions],
  );

  if (allocationDetails.isLoading || Loadable.isUninitialized(allocationDetails)) {
    return (
      <div className={classes.content}>
        <NbLoader
          horizontalCenter
          verticalCenter
        />
      </div>
    );
  }

  if (allocationDetails.hasError) {
    return (
      <div className={classes.error500Container}>
        <ErrorWidgetContent onClick={refreshAllocationDetails} />
      </div>
    );
  }

  // Handle received error codes
  if (allocationDetails.isLoaded && allocationDetails.messages.length !== 0) {
    if (
      allocationDetails.messages.some(m => m.msg === Keys.CashAction.Errors.ATVEZETES_FORRAS_SZAMLA_NEM_VALID_IDOSZAK)
    ) {
      return (
        <div className={classes.errorContainer}>
          <FeedbackBox
            type="error"
            title={translation.cashAction.allocation.error.cannotLoadAllocation}
            className={classes.errorFeedbackBox}
          />
        </div>
      );
    }

    if (
      allocationDetails.messages.some(m => m.msg === Keys.CashAction.Errors.NETBANKBROKER_ATVEZETES_NINCS_FORRAS_SZAMLA)
    ) {
      return (
        <div className={classes.errorContainer}>
          <CashActionNoSourceOrTargetAccountErrorFeedback
            cashActionType="allocation"
            selectedCurrency={selectedCurrency}
          />
        </div>
      );
    }

    // Unknown error code
    return (
      <div className={classes.error500Container}>
        <ErrorWidgetContent onClick={refreshAllocationDetails} />
      </div>
    );
  }

  // Fallback state if we get no errors and the list is empty, invalid state
  if (allocationDetails.isLoaded && allocationDetails.allocationPaymentItems.length === 0) {
    console.error('INVALID STATE: No error code has been received, yet source list is empty');
    return (
      <div className={classes.errorContainer}>
        <CashActionNoSourceOrTargetAccountErrorFeedback
          cashActionType="allocation"
          selectedCurrency={selectedCurrency}
        />
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.content}>
        <div className={classes.selectorBlock}>
          <p className={classes.selectorLabel}>{translation.cashAction.deposit.internal.currencyLabel}</p>
          <NbSelect<string>
            value={selectedCurrency}
            options={currencyOptions}
            onChange={event => handleCurrencyChange(event.target.value)}
            size="small"
            className={classes.currencySelect}
            error={noTargetAccountsAvailable}
            enforceErrorBordersWithoutMessage
          />
        </div>
        <CashActionParamEditor<AllocationPaymentItem, AllocationPaymentTargetAccount>
          amount={amount}
          cashActionType="allocation"
          numberInputError={numberInputError}
          handleAmountChange={handleAmountChange}
          selectedTargetAccount={selectedTargetAccount}
          selectedSourceAccount={selectedSourceAccount}
          selectedCurrency={selectedCurrency}
          handleTargetAccountChange={handleTargetAccountChange}
          targetAccountOptions={targetAccountOptions}
          sourceAccountOptions={sourceAccountOptions}
          handleSourceAccountChange={handleSourceAccountChange}
          sourceAccountAmount={selectedSourceAccount?.amount}
          targetAccountAmount={selectedTargetAccount?.amount ?? undefined}
          setAmountToMaxLabel={translation.cashAction.allocation.allocateAll}
          noTargetAccountsAvailable={noTargetAccountsAvailable}
        />
      </div>

      <ModalFooter
        right={{
          title: translation.cashAction.allocation.submitButtonText,
          action: handleSubmit,
          disabled:
            !isDefined(selectedSourceAccount) ||
            !isDefined(selectedTargetAccount) ||
            isDefined(numberInputError) ||
            amount === '',
        }}
      />
    </div>
  );
};
